import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/useAuth';
import { Avatar } from '@/components/ui/Avatar';
import {
  ChevronDownIcon,
  LogoutIcon,
  ShieldCheckIcon,
} from '@/components/ui/Icon';
import { useClickOutside } from '@/hooks/useClickOutside';
import { LegalDocModal } from '@/pages/login/LegalDocModal';

import styles from './UserMenu.module.css';

/**
 * Avatar trigger with a dropdown menu. Houses the user's identity (including
 * the provider NPI read straight from the login token) and the sign-out action.
 */
export function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [openLegalDoc, setOpenLegalDoc] = useState<'privacy' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!user) return null;

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar initials={user.initials} label={user.name} size={36} />
        <span className={styles.identity}>
          <span className={styles.name}>{user.name}</span>
          <span className={styles.username}>{user.username}</span>
        </span>
        <ChevronDownIcon className={styles.chevron} data-open={open} />
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.header}>
            <Avatar initials={user.initials} label={user.name} size={44} />
            <div className={styles.headerText}>
              <span className={styles.headerName}>{user.name}</span>
              <span className={styles.headerEmail}>{user.username}</span>
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.npiRow} role="status">
            <span className={styles.npiLabel}>Provider NPI</span>
            {user.npi ? (
              <span className={styles.npiValue}>{user.npi}</span>
            ) : (
              <span className={styles.npiValueEmpty}>Not assigned</span>
            )}
          </div>

          <div className={styles.divider} />

          <button
            type="button"
            className={styles.item}
            role="menuitem"
            onClick={() => {
              setOpenLegalDoc('privacy');
              setOpen(false);
            }}
          >
            <ShieldCheckIcon /> <span>Privacy Policy</span>
          </button>

          <div className={styles.divider} />

          <button
            type="button"
            className={`${styles.item} ${styles.itemDanger}`}
            role="menuitem"
            onClick={handleSignOut}
          >
            <LogoutIcon /> <span>Sign out</span>
          </button>
        </div>
      )}

      {/* Outside the menu so it stays mounted after the menu closes. */}
      <LegalDocModal docId={openLegalDoc} onClose={() => setOpenLegalDoc(null)} />
    </div>
  );
}
